import React, { ReactElement, useEffect } from 'react';
import { BaseEntite } from '../../../../../common/src/entities';
import { useAlertDialog } from '../../../Utils/dialogContext';
import { useGetQuery, useUpdateEntite } from '../../../Utils/api';
import { FormikHelpers } from 'formik';
import EnteteDePage from './EnteteDePage';
import AffichageChargement from './AffichageChargement';
import FormulaireEntite, { BasePropsDeFormulaireEntite } from './FormulaireEntite';

export type PropsDeModificationEntite<T extends BaseEntite> = BasePropsDeFormulaireEntite<T> & {
    id: number;
    properties_to_reassign?: object;
};

/** Soit properties_to_reassign un dictionnaire de { "properties1":"properties2" }
 * Cette fonction permet de remplacer la valeur gardée dans le membre "properties2" (value.properties2)
 * par la valeur provenant du membre "properties1" (value.properties1) seulement lorsque les conditions sont respectées.
 * Au final, ça permet de faire value.properties2 = value.properties1  */
function modificationAdditionelle<T>(valeur: T, properties_to_reassign: object) {
    let valeurTemp: any = valeur;
    for (let key in properties_to_reassign) {
        const properties_to_be_replaced = properties_to_reassign[key];
        const properties_replacement = key;

        if (
            valeurTemp[properties_replacement] &&
            valeurTemp[properties_replacement].length &&
            valeurTemp[properties_replacement].length != 0
        ) {
            valeurTemp[properties_to_be_replaced] = valeurTemp[properties_replacement][0].id;
        } else if (valeurTemp[properties_replacement] && valeurTemp[properties_replacement].id) {
            valeurTemp[properties_to_be_replaced] = valeurTemp[properties_replacement].id;
        } else {
            valeurTemp[properties_to_be_replaced] = null;
        }
        valeurTemp[properties_replacement] = undefined;
    }

    return valeurTemp;
}

// function removeCircularReferences(obj: any, seen = new WeakSet()): any {
//     // Handle primitives and null/undefined
//     if (obj === null || typeof obj !== 'object') {
//         return obj;
//     }

//     // Handle special objects that shouldn't be processed
//     if (obj instanceof Date || obj instanceof RegExp || obj instanceof Map || obj instanceof Set) {
//         return obj;
//     }

//     // Handle circular references
//     if (seen.has(obj)) {
//         return undefined;
//     }
//     seen.add(obj);

//     // Handle arrays
//     if (Array.isArray(obj)) {
//         return obj.map((item) => removeCircularReferences(item, seen));
//     }

//     // Handle plain objects
//     const cleanObj: any = {};
//     // Handle string keys
//     for (const key in obj) {
//         if (obj.hasOwnProperty(key)) {
//             cleanObj[key] = removeCircularReferences(obj[key], seen);
//         }
//     }
//     // Handle symbol keys
//     const symbolKeys = Object.getOwnPropertySymbols(obj);
//     for (const sym of symbolKeys) {
//         cleanObj[sym] = removeCircularReferences(obj[sym], seen);
//     }

//     return cleanObj;
// }

export default function FormulaireModificationEntite<T extends BaseEntite>(
    props: PropsDeModificationEntite<T>,
): ReactElement {
    const { id, entite, noSave, properties_to_reassign, nettoyer = (valeurs: T) => null } = props;
    const bsAlert = useAlertDialog();

    const queryEntite = useGetQuery<T>([entite.key, +id], { refetchOnWindowFocus: false });
    const [modifierEntite] = useUpdateEntite(entite);

    async function soumettreFormulaire(valeurs: T, { setSubmitting }: FormikHelpers<T>): Promise<T | null> {
        nettoyer(valeurs);
        let result: null | T = null;
        valeurs = properties_to_reassign ? modificationAdditionelle<T>(valeurs, properties_to_reassign) : valeurs;
        try {
            // result = noSave ? valeurs : (await modifierEntite(removeCircularReferences(valeurs))) ?? null;
            result = noSave ? valeurs : (await modifierEntite(valeurs)) ?? null;
        } catch (error) {
            bsAlert(`Erreur dans la mise à jour des données: ${error.message}`, 'danger');
        }
        setSubmitting(false);
        return result;
    }

    /**
     * Utilisé pour updater le UI quand on change d'une page à l'autre
     * directement avec les boutons < > dans les formulaires
     */
    useEffect(() => {
        queryEntite.isLoading = true;
        queryEntite.data = undefined;
    }, [id]);

    return (
        <div key={+id}>
            {queryEntite.data ? null : <EnteteDePage texte={'Chargement...'} />}
            <AffichageChargement query={queryEntite}>
                {({ data: dataEntite }) => {
                    return (
                        <FormulaireEntite
                            {...props}
                            valeursInitiales={dataEntite}
                            soumettreFormulaire={soumettreFormulaire}
                            sauvegardeEnToutTemps
                            texteEntete={(entite) => (
                                <>
                                    <small className="text-muted">Modifier</small>
                                    &nbsp;{entite.name}
                                </>
                            )}
                        />
                    );
                }}
            </AffichageChargement>
        </div>
    );
}
